import { useGetOne } from "ra-core";
import type { Contact, EchoflicksVideoSession } from "../types";

/**
 * Fetch the contact behind an echo so the show page can derive the attribution +
 * recurrence badges. The echo's `contact_id` is the WhatsApp id (the AWS mapper
 * falls back to `whatsapp_user_id`), and `getOne("contacts")` is the same working
 * path the Contact show page uses.
 *
 * Non-fatal by design: on error / missing id it simply returns no contact, so the
 * echo page never breaks. Guards against an upstream that ignores the id filter
 * and returns a different contact (see plan §7 G6).
 */
export function useEchoContact(record?: EchoflicksVideoSession | null): {
  contact: Contact | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const id = record?.contact_id ?? record?.whatsapp_user_id ?? undefined;
  const enabled = Boolean(id);

  const { data, isLoading, error } = useGetOne<Contact>(
    "contacts",
    { id: id as string },
    { enabled, retry: 1, staleTime: 60_000 },
  );

  const contact =
    enabled && data && String(data.id) === String(id) ? data : undefined;

  return {
    contact,
    isLoading: enabled ? isLoading : false,
    isError: Boolean(error),
  };
}
